/**
 * AdminMediaLibraryPage — Central image bank.
 *
 * /admin/media
 *   - Grid view of all uploaded images
 *   - Search by filename
 *   - Folder filter (folders are tags admins assign to assets)
 *   - Click → metadata panel (alt, folder, tags) + URL copy + delete
 *   - Upload new files
 */
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, Search, Trash2, Copy, Folder, X, Image as ImageIcon } from "lucide-react";
import api, { resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AdminLayout from "@/components/AdminLayout";

const INPUT = "w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm";

function bytes(n) {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export default function AdminMediaLibraryPage() {
    const { user, loading: authLoading } = useAuth() || {};
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [folders, setFolders] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [folder, setFolder] = useState("");
    const [selected, setSelected] = useState(null);
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        if (authLoading) return;
        if (!user || !["super_admin", "admin"].includes(user.role)) return navigate("/login");
        load();
    }, [user, authLoading, folder]); // eslint-disable-line

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (q) params.set("q", q);
            if (folder) params.set("folder", folder);
            const r = await api.get(`/admin/media?${params.toString()}`);
            setItems(r.data.items || []);
            setFolders(r.data.folders || []);
            setTotal(r.data.total || 0);
        } finally { setLoading(false); }
    };

    const upload = async (files) => {
        if (!files?.length) return;
        setUploading(true);
        try {
            for (const f of files) {
                const form = new FormData();
                form.append("file", f);
                await api.post("/admin/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
            }
            load();
        } catch (e) { alert(e.response?.data?.detail || "Yükleme başarısız"); }
        finally { setUploading(false); }
    };

    const updateMeta = async (filename, meta) => {
        await api.patch(`/admin/media/${filename}`, meta);
        load();
        setSelected({ ...selected, ...meta });
    };

    const deleteFile = async (filename) => {
        if (!window.confirm(`"${filename}" dosyasını kalıcı olarak silmek istediğinize emin misiniz? Bu dosyaya bağlı her referans bozulacak.`)) return;
        await api.delete(`/admin/media/${filename}`);
        setSelected(null);
        load();
    };

    return (
        <AdminLayout
            title="Medya Kütüphanesi"
            action={
                <>
                    <input ref={inputRef} type="file" accept="image/*" multiple onChange={(e) => upload(Array.from(e.target.files || []))} className="hidden" />
                    <button data-testid="upload-btn" onClick={() => inputRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold disabled:opacity-50">
                        <Upload className="h-4 w-4" /> {uploading ? "Yükleniyor..." : "Dosya Yükle"}
                    </button>
                </>
            }
        >
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
                <div className="relative flex-1 min-w-[240px]">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                    <input data-testid="media-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Dosya adı ara..." className="w-full pl-9 pr-3 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm" />
                </div>
                <select value={folder} onChange={(e) => setFolder(e.target.value)} className="px-3 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm">
                    <option value="">Tüm klasörler</option>
                    {folders.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
                <span className="text-xs text-slate-500 ml-auto">{total} dosya</span>
            </div>

            {loading ? (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">{[...Array(12)].map((_, i) => <div key={i} className="aspect-square rounded-xl bg-slate-100 dark:bg-slate-800 animate-pulse" />)}</div>
            ) : items.length === 0 ? (
                <div className="p-10 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-center">
                    <ImageIcon className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-slate-500 text-sm mb-4">Henüz hiç dosya yok. İlk medya dosyanızı yükleyin.</p>
                    <button onClick={() => inputRef.current?.click()} className="px-5 py-2 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold">+ Dosya Yükle</button>
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="media-grid">
                    {items.map((it) => (
                        <button key={it.filename} data-testid={`media-item-${it.filename}`} onClick={() => setSelected(it)}
                            className="group relative aspect-square rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-800 border-2 border-transparent hover:border-sky-400 transition-colors">
                            <img src={resolveImageUrl(it.url)} alt={it.alt || ""} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-900/90 to-transparent text-white px-2 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                <div className="text-[10px] font-mono truncate">{it.filename}</div>
                                <div className="text-[10px] text-white/70">{bytes(it.size)}</div>
                            </div>
                            {it.folder && (
                                <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-slate-900/70 text-white text-[9px] font-bold flex items-center gap-1">
                                    <Folder className="h-2.5 w-2.5" />{it.folder}
                                </div>
                            )}
                        </button>
                    ))}
                </div>
            )}

            {selected && (
                <MediaDetail item={selected} onClose={() => setSelected(null)} onUpdate={updateMeta} onDelete={() => deleteFile(selected.filename)} folders={folders} />
            )}
        </AdminLayout>
    );
}

function MediaDetail({ item, onClose, onUpdate, onDelete, folders }) {
    const [meta, setMeta] = useState({ alt: item.alt || "", folder: item.folder || "", tags: (item.tags || []).join(", ") });
    const [copied, setCopied] = useState(false);

    const handleSave = async () => {
        await onUpdate(item.filename, { alt: meta.alt, folder: meta.folder, tags: meta.tags });
    };
    const copy = async () => {
        await navigator.clipboard.writeText(item.url);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="w-full max-w-4xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl flex flex-col md:flex-row max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="md:w-1/2 bg-slate-100 dark:bg-slate-800 flex items-center justify-center p-4">
                    <img src={resolveImageUrl(item.url)} alt={item.alt} className="max-w-full max-h-[60vh] object-contain rounded-lg" />
                </div>
                <div className="md:w-1/2 flex flex-col">
                    <div className="p-5 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                        <h3 className="font-display text-lg font-bold truncate">Medya Detay</h3>
                        <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-4 w-4" /></button>
                    </div>
                    <div className="p-5 space-y-3 overflow-y-auto flex-1">
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Dosya adı</div>
                            <div className="font-mono text-xs text-slate-700 dark:text-slate-200 break-all">{item.filename}</div>
                        </div>
                        <div className="flex items-center gap-2">
                            <input value={item.url} readOnly className="flex-1 px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-mono" />
                            <button data-testid="copy-url" onClick={copy} className="px-3 py-2 rounded-xl bg-slate-200 dark:bg-slate-700 text-xs font-bold whitespace-nowrap"><Copy className="h-3.5 w-3.5 inline mr-1" />{copied ? "Kopyalandı!" : "URL Kopyala"}</button>
                        </div>
                        <div className="text-[11px] text-slate-500">
                            {bytes(item.size)} · {item.content_type} · {item.uploaded_at?.slice(0, 10)}
                        </div>
                        <label className="block">
                            <span className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Alt metni (SEO + erişilebilirlik)</span>
                            <input data-testid="alt-input" value={meta.alt} onChange={(e) => setMeta({ ...meta, alt: e.target.value })} className={INPUT} placeholder="Antalya sahil oteli havuz manzarası" />
                        </label>
                        <label className="block">
                            <span className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Klasör</span>
                            <input list="media-folders" value={meta.folder} onChange={(e) => setMeta({ ...meta, folder: e.target.value })} className={INPUT} placeholder="banners / hotels / blog ..." />
                            <datalist id="media-folders">{folders.map((f) => <option key={f} value={f} />)}</datalist>
                        </label>
                        <label className="block">
                            <span className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Etiketler (virgülle)</span>
                            <input value={meta.tags} onChange={(e) => setMeta({ ...meta, tags: e.target.value })} className={INPUT} placeholder="yaz, deniz, kampanya" />
                        </label>
                    </div>
                    <div className="p-5 border-t border-slate-200 dark:border-slate-700 flex justify-between gap-2">
                        <button onClick={onDelete} data-testid="delete-media" className="inline-flex items-center gap-1 px-4 py-2 rounded-xl bg-red-50 dark:bg-red-950/30 text-red-600 text-sm font-bold hover:bg-red-100"><Trash2 className="h-3.5 w-3.5" /> Sil</button>
                        <button onClick={handleSave} data-testid="save-meta" className="px-5 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold">Bilgileri Kaydet</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
